<template>
	<App title="Update Admin">
		<div class="update-layout">

			<!-- Header -->
			<div class="update-hero">
				<div class="update-avatar">{{ initials }}</div>
				<div class="update-hero-info">
					<h2 class="update-name">{{ admin.fullname }}</h2>
					<span class="update-role-badge">{{ currentRoleLabel }}</span>
				</div>
				<div class="update-hero-actions">
					<a :href="route('admin.page.single', { admin: admin.slug })" class="btn btn-sm btn-outline-secondary">
						<i class="fas fa-arrow-left me-1"></i> Back
					</a>
				</div>
			</div>

			<!-- Form card -->
			<div class="update-card">
				<div class="update-card-header">
					<i class="fas fa-user-edit me-2 text-muted"></i>
					<span>Edit Profile</span>
				</div>

				<Form ref="form" @submit="onSubmit" @submitting="onSubmitting">
					<div class="update-form-grid">

						<div class="update-field">
							<label for="firstname" class="update-label">First Name</label>
							<InputField id="firstname" v-model="firstname" name="firstname" rules="required" label="Firstname" placeholder="First name" />
						</div>

						<div class="update-field">
							<label for="lastname" class="update-label">Last Name</label>
							<InputField id="lastname" v-model="lastname" name="lastname" rules="required" label="Lastname" placeholder="Last name" />
						</div>

						<div class="update-field">
							<label for="email" class="update-label">Email Address</label>
							<InputField id="email" v-model="email" name="email" rules="required|email" label="Email" type="email" placeholder="Email address" />
						</div>

						<div class="update-field">
							<label for="phone" class="update-label">Mobile Number</label>
							<PhoneField id="phone" v-model="phone" name="phone" rules="required|phone" label="Phone" placeholder="Phone number" />
						</div>

						<div class="update-field">
							<label for="role" class="update-label">Access Role</label>
							<SelectField id="role" v-model="role" name="role" rules="required" label="Role" placeholder="Role" :options="options">
								<option v-for="r in roles" :key="r.value" :value="r.value">{{ r.name }}</option>
							</SelectField>
						</div>

						<div v-if="locationConfig" class="update-field">
							<label :for="locationConfig.name" class="update-label">{{ locationConfig.label }}</label>
							<SelectField
								id="location"
								v-model="location"
								name="location"
								rules="required"
								:label="locationConfig.label"
								:placeholder="`Select ${locationConfig.label}`"
								:options="locationConfig.options"
							/>
						</div>

					</div>

					<div class="update-form-actions">
						<button type="button" @click="goBack" class="btn btn-outline-danger">
							<i class="fas fa-times me-1"></i> Cancel
						</button>
						<Button id="action" type="submit" :label="labels" :loading="loading" :done="done" :disabled="checkInput" class="btn btn-primary" />
					</div>
				</Form>
			</div>

		</div>

		<div class="mt-9 position-absolute top-0 end-0">
			<NotivueKeyboard v-slot="{ containersTabIndex }">
				<Notivue v-slot="item" :containersTabIndex="containersTabIndex">
					<AppNotification :item="item" />
				</Notivue>
			</NotivueKeyboard>
		</div>
	</App>
</template>

<script lang="ts">
import { useConfirm } from '@/libs/useConfirm.ts'
import Swal from 'sweetalert2'
import type Admin from '@/models/Admin'
import { Roles } from '@/models/Admin'
import { error } from '@/ts/app'
import Alert from '@/vue/components/Alert.vue'
import Block from '@/vue/components/Block.vue'
import Button from '@/vue/components/Button.vue'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import PhoneField from '@/vue/components/PhoneField.vue'
import SelectField from '@/vue/components/SelectField.vue'
import App from '@/vue/templates/App.vue'
import { ButtonLabel } from '@/vue/utils/Button'
import { AxiosError } from 'axios'
import { Options as Select2Options } from 'select2'
import { Component, Prop, Ref, Vue, Watch, toNative } from 'vue-facing-decorator'
import { Notivue, Notification, push, materialTheme, NotificationProgress, NotivueKeyboard } from 'notivue'
import AppNotification from '@/vue/components/AppNotification.vue'
import $ from 'jquery'

@Component({
	components: {
		App,
		Form,
		Alert,
		InputField,
		PhoneField,
		Button,
		Block,
		SelectField,
		Notivue,
		Notification,
		NotificationProgress,
		NotivueKeyboard,
		AppNotification,
	},
})
class Update extends Vue {
	message = ''

	firstname = ''
	lastname = ''
	email = ''
	phone = ''
	role = ''
	location= '' 

	confirm = useConfirm().confirm
	loading = false
	done = false

	@Ref('form')
	readonly form!: InstanceType<typeof Form>

	@Ref('alert')
	readonly alert!: InstanceType<typeof Alert>

	@Prop({ type: Object })
	readonly admin!: Admin

	@Prop({ type: Array })
	readonly roles!: Roles[]

	@Watch('message')
	onMessageChanged(value: string) {
		if (value.length > 0) {
			this.alert.show()
		} else {
			this.alert.hide()
		}
	}

	@Watch('role')
	onRoleChanged(newRole: string) {
		// Clear location value when role changes
		this.location = ''
	}

	// Role to location mapping
	get roleLocationMap(){
		return {
			'station_manager': { 
				name: 'station', 
				label: 'Station', 
				route: 'station.ajax.select.station',
				labelKey: 'title'
			},
			'station_officer': { 
				name: 'station', 
				label: 'Station', 
				route: 'station.ajax.select.station',
				labelKey: 'title'
			},
			'cluster_manager': { 
				name: 'cluster', 
				label: 'Cluster', 
				route: 'cluster.ajax.select.cluster',
				labelKey: 'name'
			},
			'zonal_manager': { 
				name: 'zonal', 
				label: 'Zonal', 
				route: 'zone.ajax.select.zone',
				labelKey: 'name'
			},
			'state_manager': { 
				name: 'state', 
				label: 'State', 
				route: 'state.ajax.select.state',
				labelKey: 'name'
			},
			'regional_manager': { 
				name: 'regional', 
				label: 'Regional', 
				route: 'region.ajax.select.region',
				labelKey: 'name'
			},
			'country_manager': { 
				name: 'country', 
				label: 'Country', 
				route: 'country.ajax.select.country',
				labelKey: 'name'
			},
			'warehouse_manager': { 
				name: 'warehouse', 
				label: 'Warehouse', 
				route: 'warehouse.ajax.select.warehouse',
				labelKey: 'name'
			},
			'financier': { 
				name: 'financier', 
				label: 'Financier', 
				route: 'financier.ajax.select.financier',
				labelKey: 'title'
			},
		}
	}

	// Get location configuration for current role
	get locationConfig() {
		const config = this.roleLocationMap[this.role]
		if (!config) return null

		const labelKey = config.labelKey || 'name'

		return {
			name: config.name,
			label: config.label,
			options: {
				placeholder: `Select ${config.label}`,
				minimumResultsForSearch: 3,
				ajax: {
					method: 'get',
					url: this.route(config.route),
				},
				//@ts-ignore
				templateResult: ({ id, title, label, text }) => (id ? $(`<span>${title}</span>`) : text),
				//@ts-ignore
				templateSelection: ({ id, title, text }) => (id ? $(`<span>${title}</span>`) : text),
			} as Select2Options
		}
	}

	get initials() {
		return (this.admin?.fullname ?? '')
			.split(' ')
			.slice(0, 2)
			.map((w) => w[0]?.toUpperCase() ?? '')
			.join('')
	}

	get currentRoleLabel() {
		return (this.role || this.admin?.role || '')
			.replace(/_/g, ' ')
			.replace(/\b\w/g, (c) => c.toUpperCase())
	}

	get labels(): ButtonLabel {
		return {
			normal: 'Update Admin',
			loading: 'Updating....Admin',
			done: 'Updated Admin',
		}
	}

	get checkInput(): boolean {
		return [this.firstname, this.lastname, this.email, this.phone, this.role].some((field) => field.trim() === '')
	}


	get options(): Select2Options {
		return {
			placeholder: 'Select Role',
			minimumResultsForSearch: Infinity,
			//@ts-ignore
			ajax: null,
		}
	}

	async confirmAction() {
		const isConfirmed = await this.confirm(
			'Are you sure you want to cancel this operation?',
			{
				title: 'Cancel Process',
				confirmText: 'Yes, Cancel',
				cancelText: 'No, Continue',
			}
		)

		if (!isConfirmed) return

		setTimeout(() => {
			this.loading = false
			this.router.visit(this.route('admin.page.group'))
		}, 300)
	}

	created() {
		this.firstname = this.admin.firstname
		this.lastname = this.admin.lastname
		this.email = this.admin.email
		this.phone = this.admin.phone
		this.role = this.admin.role
		
		// Set existing location data dynamically based on role
		const config = this.roleLocationMap[this.role]
		if (config && this.admin[config.name]) {
			this.location= this.admin[config.name]
		}
	}

	onSubmit(payload) {
		this.loading = true
		this.$http
			.put(this.route('admin.ajax.update', { admin: this.admin.slug }), payload)
			.then((response) => {
				this.done = true
				this.message = 'Admin updated successfully'
				Swal.fire({
					title: 'Success!',
					text: this.message,
					icon: 'success',
					confirmButtonText: 'OK',
				}).then(() => {
					this.router.visit((response.data.admin as Admin).url.single)
					this.message = ''
					Swal.close()
				})
			})
			.catch((e: AxiosError) => error(e, this))
			.finally(() => (this.loading = false))
	}

	onSubmitting() {
		// Handle form submitting state if needed
	}

	goBack() {
		window.history.back()
	}
}

export default toNative(Update)
</script>

<style scoped lang="scss">
.update-layout {
	max-width: 760px;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
.update-hero {
	display: flex;
	align-items: center;
	gap: 1.25rem;
	background: #fff;
	border-radius: 12px;
	padding: 1.5rem 1.75rem;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	flex-wrap: wrap;
}

.update-avatar {
	flex-shrink: 0;
	width: 64px;
	height: 64px;
	border-radius: 50%;
	background: linear-gradient(135deg, #1e3a2f, #3a7d52);
	color: #fff;
	font-size: 1.35rem;
	font-weight: 700;
	display: flex;
	align-items: center;
	justify-content: center;
	letter-spacing: 0.02em;
	box-shadow: 0 4px 12px rgba(30, 58, 47, 0.25);
}

.update-hero-info {
	flex: 1;
	min-width: 0;
}

.update-name {
	font-size: 1.15rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.3rem;
	line-height: 1.2;
}

.update-role-badge {
	display: inline-block;
	padding: 0.2rem 0.7rem;
	border-radius: 999px;
	font-size: 0.72rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	background: #dcfce7;
	color: #15803d;
}

.update-hero-actions {
	flex-shrink: 0;
	margin-left: auto;
}

// ─── Form card ────────────────────────────────────────────────────────────────
.update-card {
	background: #fff;
	border-radius: 12px;
	border: 1px solid #e9ecef;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
	overflow: hidden;
}

.update-card-header {
	padding: 0.875rem 1.75rem;
	border-bottom: 1px solid #f1f3f5;
	font-size: 0.85rem;
	font-weight: 600;
	color: #374151;
	background: #f9fafb;
	display: flex;
	align-items: center;
}

.update-form-grid {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 1.25rem;
	padding: 1.5rem 1.75rem;

	@media (max-width: 560px) {
		grid-template-columns: 1fr;
	}
}

.update-field {
	display: flex;
	flex-direction: column;
	gap: 0.35rem;
}

.update-label {
	font-size: 0.78rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #6b7280;
	margin-bottom: 0;
}

// ─── Actions ──────────────────────────────────────────────────────────────────
.update-form-actions {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 0.625rem;
	padding: 1rem 1.75rem;
	border-top: 1px solid #f1f3f5;
	background: #f9fafb;
}
</style>